import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { sesionesTrabajoAPI, animalesAPI } from '../services/api';
import { buscarAnimalOffline, submitOffline } from '../services/offlineHelpers';
import { addAnimalToCache, addPendingOp } from '../services/offlineDB';
import { calcularEdadMeses } from '../utils/helpers';
import { sonidoExito, sonidoNoEncontrado, sonidosActivos, setSonidosActivos } from '../utils/sonidos';
import FormPesaje from '../components/Sesiones/FormPesaje';
import FormServicio from '../components/Sesiones/FormServicio';
import FormParicion from '../components/Sesiones/FormParicion';
import FormControlMacho from '../components/Sesiones/FormControlMacho';
import FormSanitario from '../components/Sesiones/FormSanitario';
import FormDestete from '../components/Sesiones/FormDestete';

const TIPOS_SESION = [
  { id: 'pesaje', nombre: 'Pesaje', desc: 'Registrar peso, altura cadera y frame', color: 'green' },
  { id: 'servicio', nombre: 'Servicio', desc: 'IATF o servicio natural', color: 'blue' },
  { id: 'paricion', nombre: 'Paricion', desc: 'Nacimientos y destetes', color: 'purple' },
  { id: 'destete', nombre: 'Destete', desc: 'Peso ternero + madre al destete, calcula EP', color: 'green' },
  { id: 'control_macho', nombre: 'Control Machos', desc: 'Andrologia, CE y scores', color: 'orange' },
  { id: 'vacunacion', nombre: 'Vacunacion', desc: 'Vacunas y antiparasitarios', color: 'green' },
  { id: 'enfermedad', nombre: 'Enfermedad', desc: 'Diagnostico y tratamiento', color: 'orange' },
  { id: 'medicamento', nombre: 'Medicamento', desc: 'Aplicacion de medicamentos', color: 'blue' },
  { id: 'mortandad', nombre: 'Mortandad', desc: 'Registro de bajas', color: 'red' },
  { id: 'lesion', nombre: 'Lesion', desc: 'Lesiones y heridas', color: 'orange' },
  { id: 'observacion', nombre: 'Observacion', desc: 'Observaciones generales', color: 'gray' },
];

const SesionesTrabajoPage = () => {
  const { user } = useAuth();
  const [vista, setVista] = useState('lista'); // lista | nueva | activa | detalle
  const [sesiones, setSesiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sesionActiva, setSesionActiva] = useState(null);
  const [sesionDetalle, setSesionDetalle] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Nueva sesion form
  const [nombreSesion, setNombreSesion] = useState('');
  const [tipoSesion, setTipoSesion] = useState('');

  // Carga continua state
  const [scanInput, setScanInput] = useState('');
  const [animalEncontrado, setAnimalEncontrado] = useState(null);
  const [animalNoEncontrado, setAnimalNoEncontrado] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [registros, setRegistros] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [sonidoOn, setSonidoOn] = useState(sonidosActivos());
  const toggleSonido = () => { const v = !sonidoOn; setSonidoOn(v); setSonidosActivos(v); if (v) sonidoExito(); };
  const scanInputRef = useRef(null);

  // Form data for current registration
  const [formDatos, setFormDatos] = useState({});
  const [newAnimalData, setNewAnimalData] = useState({ sexo: '', raza: 'Brangus', categoria: '', fecha_nacimiento: '', color: '' });

  const cargarSesiones = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    try {
      setLoading(true);
      const res = await sesionesTrabajoAPI.listar(user.establecimiento_id);
      setSesiones(res.data.sesiones || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [user?.establecimiento_id]);

  useEffect(() => { cargarSesiones(); }, [cargarSesiones]);

  const descargar = async (s, formato) => {
    setError('');
    try {
      if (formato === 'excel') await sesionesTrabajoAPI.descargarExcel(s.id, s.nombre);
      else await sesionesTrabajoAPI.descargarPdf(s.id, s.nombre);
    } catch {
      setError(`No se pudo descargar el ${formato === 'excel' ? 'Excel' : 'PDF'}. Verifica tu conexion.`);
    }
  };

  // Auto-focus scan input when in active session
  useEffect(() => {
    if (vista === 'activa' && scanInputRef.current && !animalEncontrado && !animalNoEncontrado) {
      scanInputRef.current.focus();
    }
  }, [vista, animalEncontrado, animalNoEncontrado, registros]);

  const crearSesion = async () => {
    if (!nombreSesion.trim() || !tipoSesion) return;
    try {
      if (navigator.onLine) {
        const res = await sesionesTrabajoAPI.crear({
          nombre: nombreSesion.trim(),
          tipo: tipoSesion,
          establecimiento_id: user.establecimiento_id,
          usuario_id: user.id,
        });
        setSesionActiva(res.data.sesion);
      } else {
        // Offline: create local session
        const localSesion = {
          id: `offline_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          nombre: nombreSesion.trim(),
          tipo: tipoSesion,
          establecimiento_id: user.establecimiento_id,
          usuario_id: user.id,
          estado: 'activa',
          registros: [],
          total_animales: 0,
          created_at: new Date().toISOString(),
          _offline: true,
        };
        setSesionActiva(localSesion);
      }
      setRegistros([]);
      setVista('activa');
      setMensaje('');
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear sesion');
    }
  };

  const buscarAnimal = async () => {
    if (!scanInput.trim()) return;
    setBuscando(true);
    setError('');
    setAnimalEncontrado(null);
    setAnimalNoEncontrado(false);
    setFormDatos({});
    try {
      const result = await buscarAnimalOffline(scanInput.trim(), user.establecimiento_id);
      setAnimalEncontrado(result.animal);
      // Pre-fill fecha
      setFormDatos({ fecha: new Date().toISOString().split('T')[0] });
    } catch {
      setAnimalNoEncontrado(true);
      sonidoNoEncontrado();
      setNewAnimalData({ sexo: '', raza: 'Brangus', categoria: '', fecha_nacimiento: '', color: '' });
      setFormDatos({ fecha: new Date().toISOString().split('T')[0] });
    } finally { setBuscando(false); }
  };

  const guardarRegistroExistente = async () => {
    if (!animalEncontrado) return;
    setGuardando(true);
    setError('');
    try {
      // Register the actual event in its proper collection
      if (sesionActiva.tipo === 'pesaje' && formDatos.peso) {
        const edadMeses = calcularEdadMeses(animalEncontrado.fecha_nacimiento);
        await submitOffline('/api/pesajes/', {
          animal_id: animalEncontrado.id,
          establecimiento_id: user.establecimiento_id,
          fecha: formDatos.fecha || new Date().toISOString().split('T')[0],
          edad_meses: edadMeses || 0,
          peso: parseFloat(formDatos.peso),
          altura_cadera: formDatos.altura_cadera ? parseFloat(formDatos.altura_cadera) : null,
          frame: formDatos.frame ? parseInt(formDatos.frame) : null,
          condicion_corporal: formDatos.condicion_corporal ? parseInt(formDatos.condicion_corporal) : null,
          lote: formDatos.lote || null,
          origen: 'sesion',
          observaciones: formDatos.observaciones || null,
        });
      } else if (sesionActiva.tipo === 'servicio' && formDatos.tipo_servicio) {
        await submitOffline('/api/servicios/', {
          hembra_id: animalEncontrado.id,
          establecimiento_id: user.establecimiento_id,
          fecha: formDatos.fecha_servicio || new Date().toISOString().split('T')[0],
          tipo_servicio: formDatos.tipo_servicio,
          toro_id: formDatos.toro || null,
          resultado: formDatos.resultado || null,
          fecha_confirmacion: formDatos.fecha_confirmacion || null,
          dias_gestacion: formDatos.dias_gestacion ? parseInt(formDatos.dias_gestacion) : 280,
          temporada: formDatos.temporada || null,
          observaciones: formDatos.observaciones || null,
        });
      } else if (sesionActiva.tipo === 'paricion') {
        await submitOffline('/api/pariciones/', {
          madre_id: animalEncontrado.id,
          establecimiento_id: user.establecimiento_id,
          fecha_paricion: formDatos.fecha || new Date().toISOString().split('T')[0],
          sexo_ternero: formDatos.sexo_cria || 'macho',
          peso_nacimiento: formDatos.peso_nacimiento ? parseFloat(formDatos.peso_nacimiento) : null,
          tipo: formDatos.tipo_parto || 'normal',
          dificultad: formDatos.dificultad || 'sin_ayuda',
          fecha_destete: formDatos.fecha_destete || null,
          peso_destete: formDatos.peso_destete ? parseFloat(formDatos.peso_destete) : null,
          ternero_VID: formDatos.cria_vid || null,
          observaciones: formDatos.observaciones || null,
        });
      } else if (sesionActiva.tipo === 'control_macho' && formDatos.ce) {
        const edadMeses = calcularEdadMeses(animalEncontrado.fecha_nacimiento);
        await submitOffline('/api/controles_machos/', {
          macho_id: animalEncontrado.id,
          establecimiento_id: user.establecimiento_id,
          fecha: formDatos.fecha || new Date().toISOString().split('T')[0],
          edad_meses: edadMeses || 0,
          ce_absoluta: parseFloat(formDatos.ce),
          apto_reproductor: formDatos.aptitud === 'apto' ? true : formDatos.aptitud === 'no_apto' ? false : null,
          libido: formDatos.libido || null,
          conformacion: formDatos.conformacion || null,
          calidad_seminal: formDatos.calidad_seminal || null,
          motilidad: formDatos.motilidad ? parseFloat(formDatos.motilidad) : null,
          concentracion: formDatos.concentracion ? parseFloat(formDatos.concentracion) : null,
          observaciones: formDatos.observaciones || null,
        });
      } else if (['vacunacion','enfermedad','medicamento','mortandad','lesion','observacion'].includes(sesionActiva.tipo)) {
        await submitOffline('/api/eventos-sanitarios/', {
          animal_id: animalEncontrado.id,
          establecimiento_id: user.establecimiento_id,
          fecha: formDatos.fecha || new Date().toISOString().split('T')[0],
          tipo: sesionActiva.tipo,
          causa: formDatos.causa || null,
          enfermedad: formDatos.enfermedad || null,
          vacuna: formDatos.vacuna || null,
          tratamiento: formDatos.tratamiento || null,
          dosis: formDatos.dosis || null,
          gravedad: formDatos.gravedad || null,
          ubicacion_lesion: formDatos.ubicacion_lesion || null,
          detalle: formDatos.detalle || null,
          observaciones: formDatos.observaciones || null,
        });
        // If mortandad, update animal status
        if (sesionActiva.tipo === 'mortandad') {
          try {
            await animalesAPI.update(animalEncontrado.id, { estado: 'muerto' });
          } catch { /* silent */ }
        }
      } else if (sesionActiva.tipo === 'destete' && formDatos.peso_ternero) {
        await submitOffline('/api/destete/', {
          ternero_id: animalEncontrado.id,
          establecimiento_id: user.establecimiento_id,
          peso_ternero: parseFloat(formDatos.peso_ternero),
          peso_madre: formDatos.peso_madre ? parseFloat(formDatos.peso_madre) : null,
          madre_vid: formDatos.madre_vid || null,
          cc_madre: formDatos.cc_madre ? parseInt(formDatos.cc_madre) : null,
          fecha_destete: formDatos.fecha_destete || new Date().toISOString().split('T')[0],
          observaciones: formDatos.observaciones || null,
        });
      }

      // Add to session record (online: API, offline: local only)
      if (navigator.onLine && sesionActiva.id && !sesionActiva.id.startsWith('offline_')) {
        const regRes = await sesionesTrabajoAPI.agregarRegistro(sesionActiva.id, {
          sesion_id: sesionActiva.id,
          animal_id: animalEncontrado.id,
          animal_vid: animalEncontrado.VID || scanInput.trim(),
          animal_nuevo: false,
          datos: formDatos,
        });
        setRegistros(prev => [...prev, { animal_vid: animalEncontrado.VID || scanInput.trim(), animal_nuevo: false, datos: formDatos, ...regRes.data.registro }]);
      } else {
        // Offline: only add to local registros
        setRegistros(prev => [...prev, { id: `local_${Date.now()}`, animal_vid: animalEncontrado.VID || scanInput.trim(), animal_nuevo: false, datos: formDatos }]);
      }

      const offlineMsg = !navigator.onLine ? ' (GUARDADO LOCAL)' : '';
      setMensaje(`VID ${animalEncontrado.VID} registrado${offlineMsg} (${registros.length + 1} animales)`);
      sonidoExito();
      resetParaSiguiente();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Error al guardar registro');
    } finally { setGuardando(false); }
  };

  const guardarRegistroNuevo = async () => {
    if (!newAnimalData.sexo) { setError('Sexo es obligatorio'); return; }
    setGuardando(true);
    setError('');
    try {
      const animalPayload = {
        VID: scanInput.trim(),
        sexo: newAnimalData.sexo,
        raza: newAnimalData.raza || 'Brangus',
        categoria: newAnimalData.categoria || (newAnimalData.sexo === 'hembra' ? 'vaquilla' : 'novillo'),
        fecha_nacimiento: newAnimalData.fecha_nacimiento || new Date().toISOString().split('T')[0],
        color: newAnimalData.color || null,
        origen: 'compra',
        establecimiento_id: user.establecimiento_id,
      };

      let newAnimalId;
      let offlineMode = false;

      if (navigator.onLine) {
        // ONLINE: create via API
        const createRes = await animalesAPI.create(animalPayload);
        newAnimalId = createRes.data.animal_id;
      } else {
        // OFFLINE: create local ID, save to IndexedDB, queue for sync
        offlineMode = true;
        newAnimalId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const localAnimal = { ...animalPayload, id: newAnimalId, estado: 'activo', fecha_ingreso: new Date().toISOString().split('T')[0] };
        await addAnimalToCache(localAnimal);
        await addPendingOp({ method: 'POST', url: '/animales/', data: { ...animalPayload, offline_id: newAnimalId } });
      }

      // Register event based on session type
      if (sesionActiva.tipo === 'pesaje' && formDatos.peso) {
        const edadMeses = newAnimalData.fecha_nacimiento ? calcularEdadMeses(newAnimalData.fecha_nacimiento) : 0;
        await submitOffline('/api/pesajes/', {
          animal_id: newAnimalId,
          establecimiento_id: user.establecimiento_id,
          fecha: formDatos.fecha || new Date().toISOString().split('T')[0],
          edad_meses: edadMeses,
          peso: parseFloat(formDatos.peso),
          altura_cadera: formDatos.altura_cadera ? parseFloat(formDatos.altura_cadera) : null,
          frame: formDatos.frame ? parseInt(formDatos.frame) : null,
          condicion_corporal: formDatos.condicion_corporal ? parseInt(formDatos.condicion_corporal) : null,
          lote: formDatos.lote || null,
          origen: 'sesion',
          observaciones: formDatos.observaciones || null,
        });
      } else if (sesionActiva.tipo === 'servicio' && formDatos.tipo_servicio) {
        await submitOffline('/api/servicios/', {
          hembra_id: newAnimalId,
          establecimiento_id: user.establecimiento_id,
          fecha: formDatos.fecha_servicio || new Date().toISOString().split('T')[0],
          tipo_servicio: formDatos.tipo_servicio,
          toro_id: formDatos.toro || null,
          resultado: formDatos.resultado || null,
          fecha_confirmacion: formDatos.fecha_confirmacion || null,
          dias_gestacion: formDatos.dias_gestacion ? parseInt(formDatos.dias_gestacion) : 280,
          temporada: formDatos.temporada || null,
          observaciones: formDatos.observaciones || null,
        });
      } else if (sesionActiva.tipo === 'control_macho' && formDatos.ce) {
        const edadMeses = newAnimalData.fecha_nacimiento ? calcularEdadMeses(newAnimalData.fecha_nacimiento) : 0;
        await submitOffline('/api/controles_machos/', {
          macho_id: newAnimalId,
          establecimiento_id: user.establecimiento_id,
          fecha: formDatos.fecha || new Date().toISOString().split('T')[0],
          edad_meses: edadMeses,
          ce_absoluta: parseFloat(formDatos.ce),
          apto_reproductor: formDatos.aptitud === 'apto' ? true : formDatos.aptitud === 'no_apto' ? false : null,
          libido: formDatos.libido || null,
          conformacion: formDatos.conformacion || null,
          calidad_seminal: formDatos.calidad_seminal || null,
          motilidad: formDatos.motilidad ? parseFloat(formDatos.motilidad) : null,
          concentracion: formDatos.concentracion ? parseFloat(formDatos.concentracion) : null,
          observaciones: formDatos.observaciones || null,
        });
      }

      // Add to session record (online: API, offline: local only)
      if (navigator.onLine && sesionActiva.id && !sesionActiva.id.startsWith('offline_')) {
        const regRes = await sesionesTrabajoAPI.agregarRegistro(sesionActiva.id, {
          sesion_id: sesionActiva.id,
          animal_id: newAnimalId,
          animal_vid: scanInput.trim(),
          animal_nuevo: true,
          datos: { ...formDatos, ...newAnimalData },
        });
        setRegistros(prev => [...prev, { animal_vid: scanInput.trim(), animal_nuevo: true, datos: { ...formDatos, ...newAnimalData }, ...regRes.data.registro }]);
      } else {
        // Offline: add to local registros only
        setRegistros(prev => [...prev, { id: `local_${Date.now()}`, animal_vid: scanInput.trim(), animal_nuevo: true, datos: { ...formDatos, ...newAnimalData } }]);
      }

      const msg = offlineMode
        ? `NUEVO ${scanInput.trim()} guardado LOCAL (se sincronizara con internet) — ${registros.length + 1} animales`
        : `NUEVO animal ${scanInput.trim()} creado y registrado (${registros.length + 1} animales)`;
      setMensaje(msg);
      sonidoExito();
      resetParaSiguiente();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Error al crear animal/registro');
    } finally { setGuardando(false); }
  };

  const resetParaSiguiente = () => {
    setScanInput('');
    setAnimalEncontrado(null);
    setAnimalNoEncontrado(false);
    setFormDatos({});
    setNewAnimalData({ sexo: '', raza: 'Brangus', categoria: '', fecha_nacimiento: '', color: '' });
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const finalizarSesion = async () => {
    try {
      await sesionesTrabajoAPI.finalizar(sesionActiva.id);
      setVista('lista');
      setSesionActiva(null);
      cargarSesiones();
    } catch { /* silent */ }
  };

  const eliminarSesion = async (sesionId) => {
    try {
      await sesionesTrabajoAPI.eliminar(sesionId);
      setDeleteConfirm(null);
      setSesionDetalle(null);
      cargarSesiones();
      if (vista === 'detalle') setVista('lista');
    } catch { /* silent */ }
  };

  const verDetalle = async (sesion) => {
    try {
      const res = await sesionesTrabajoAPI.obtener(sesion.id);
      setSesionDetalle(res.data.sesion);
      setVista('detalle');
    } catch { /* silent */ }
  };

  const reanudarSesion = (sesion) => {
    setSesionActiva(sesion);
    setRegistros(sesion.registros || []);
    setVista('activa');
  };

  // ─── RENDER: LISTA ─────────────────────────
  if (vista === 'lista') {
    return (
      <div className="p-4 sm:p-6 space-y-6" data-testid="sesiones-trabajo-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sesiones de Trabajo</h1>
            <p className="text-sm text-gray-500 mt-1">Gestiona tus jornadas de pesaje, servicio, paricion y control</p>
          </div>
          <button
            onClick={() => setVista('nueva')}
            className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm shadow-sm"
            data-testid="btn-nueva-sesion"
          >
            + Nueva Sesion
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sesiones.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-gray-600 font-medium">No hay sesiones registradas</p>
            <p className="text-sm text-gray-400 mt-1">Crea una nueva sesion para empezar a registrar animales</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sesiones.map((s) => (
              <div key={s.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition" data-testid={`sesion-item-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 cursor-pointer flex-1" onClick={() => verDetalle(s)}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold ${
                      s.tipo === 'pesaje' ? 'bg-green-500' :
                      s.tipo === 'servicio' ? 'bg-blue-500' :
                      s.tipo === 'paricion' ? 'bg-purple-500' : 'bg-orange-500'
                    }`}>
                      {s.tipo === 'pesaje' ? 'P' : s.tipo === 'servicio' ? 'S' : s.tipo === 'paricion' ? 'Pa' : 'M'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{s.nombre}</p>
                      <p className="text-xs text-gray-500">
                        {s.tipo.charAt(0).toUpperCase() + s.tipo.slice(1)} | {s.created_at?.slice(0, 10)} | {s.total_animales} animales
                        {s.estado === 'activa' && <span className="ml-2 text-green-600 font-medium">EN CURSO</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.estado === 'activa' && (
                      <button onClick={() => reanudarSesion(s)} className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100" data-testid={`reanudar-${s.id}`}>
                        Reanudar
                      </button>
                    )}
                    <button onClick={() => descargar(s, 'excel')} className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-medium hover:bg-blue-100" data-testid={`excel-${s.id}`}>
                      Excel
                    </button>
                    <button onClick={() => descargar(s, 'pdf')} className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100" data-testid={`pdf-${s.id}`}>
                      PDF
                    </button>
                    <button onClick={() => setDeleteConfirm(s.id)} className="px-3 py-1.5 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg text-xs hover:bg-red-50 hover:text-red-600 hover:border-red-200" data-testid={`delete-${s.id}`}>
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete confirm modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Eliminar Sesion</h3>
              <p className="text-sm text-gray-600 mb-4">Esta accion no se puede deshacer. Se eliminara la sesion y todos sus registros.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm">Cancelar</button>
                <button onClick={() => eliminarSesion(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="confirm-delete-btn">Eliminar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── RENDER: NUEVA SESION ───────────────────
  if (vista === 'nueva') {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="nueva-sesion-form">
        <button onClick={() => setVista('lista')} className="text-gray-500 hover:text-gray-700 mb-4 text-sm flex items-center gap-1">
          ← Volver
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Iniciar Nueva Sesion</h1>

        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la sesion *</label>
            <input
              type="text"
              value={nombreSesion}
              onChange={(e) => setNombreSesion(e.target.value)}
              placeholder="Ej: Pesaje Lote Norte, Compra Remate Mayo..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              autoFocus
              data-testid="input-nombre-sesion"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de sesion *</label>
            <div className="grid grid-cols-2 gap-3">
              {TIPOS_SESION.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTipoSesion(t.id)}
                  className={`p-4 rounded-xl border-2 text-left transition ${
                    tipoSesion === t.id
                      ? `border-${t.color}-500 bg-${t.color}-50 ring-2 ring-${t.color}-200`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`tipo-${t.id}`}
                >
                  <p className="font-semibold text-gray-900">{t.nombre}</p>
                  <p className="text-xs text-gray-500 mt-1">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

          <button
            onClick={crearSesion}
            disabled={!nombreSesion.trim() || !tipoSesion}
            className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="btn-iniciar-sesion"
          >
            Iniciar Sesion
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDER: SESION ACTIVA (CARGA CONTINUA) ──
  if (vista === 'activa' && sesionActiva) {
    return (
      <div className="p-4 sm:p-6" data-testid="sesion-activa">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">
              {sesionActiva.nombre}
              {!navigator.onLine && <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-bold">MODO CAMPO</span>}
            </h1>
            <p className="text-xs text-gray-500">{sesionActiva.tipo.charAt(0).toUpperCase() + sesionActiva.tipo.slice(1)} | {registros.length} animales registrados</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleSonido}
              title={sonidoOn ? 'Silenciar sonidos' : 'Activar sonidos'}
              className={`p-2 rounded-lg border text-sm ${sonidoOn ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
              data-testid="btn-toggle-sonido"
              aria-pressed={sonidoOn}
            >
              {sonidoOn ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14" /></svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" /></svg>
              )}
            </button>
            <button
              onClick={finalizarSesion}
              className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100"
              data-testid="btn-finalizar-sesion"
            >
              Finalizar Sesion
            </button>
          </div>
        </div>

        {/* Messages */}
        {mensaje && <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-testid="msg-success">{mensaje}</div>}
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="msg-error">{error}</div>}

        {/* Scan input - always visible when no animal is being processed */}
        {!animalEncontrado && !animalNoEncontrado && (
          <div className="bg-gray-900 rounded-xl p-5 mb-4">
            <p className="text-gray-400 text-xs mb-2 font-medium uppercase tracking-wider">Escanear o escribir VID/IDE del animal</p>
            <div className="flex gap-2">
              <input
                ref={scanInputRef}
                type="text"
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && buscarAnimal()}
                placeholder="Apunte el lector y escanee, o escriba VID + Enter..."
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 placeholder-gray-500"
                autoFocus
                data-testid="scan-input"
              />
              <button
                onClick={buscarAnimal}
                disabled={buscando || !scanInput.trim()}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                data-testid="btn-buscar-animal"
              >
                {buscando ? '...' : 'Buscar'}
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-2">Si el animal no existe, podras crearlo y registrarlo al instante</p>
          </div>
        )}

        {/* Animal encontrado → form de datos */}
        {animalEncontrado && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4 space-y-4">
            <div className="flex justify-between items-start">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex-1">
                <p className="text-xs font-semibold text-green-700 mb-1">Animal encontrado</p>
                <div className="flex gap-6 text-sm">
                  <span><strong>VID:</strong> {animalEncontrado.VID}</span>
                  <span><strong>Sexo:</strong> {animalEncontrado.sexo}</span>
                  <span><strong>Categoria:</strong> {animalEncontrado.categoria}</span>
                  {animalEncontrado.raza && <span><strong>Raza:</strong> {animalEncontrado.raza}</span>}
                </div>
              </div>
              <button onClick={resetParaSiguiente} className="ml-3 text-gray-400 hover:text-red-500 text-sm">Cancelar</button>
            </div>

            {/* Form fields based on session type */}
            {renderFormCampos()}

            <div className="flex justify-end">
              <button
                onClick={guardarRegistroExistente}
                disabled={guardando}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                data-testid="btn-guardar-registro"
              >
                {guardando ? 'Guardando...' : 'Guardar y Siguiente'}
              </button>
            </div>
          </div>
        )}

        {/* Animal NO encontrado → form nuevo */}
        {animalNoEncontrado && (
          <div className="bg-white border-2 border-amber-300 rounded-xl p-5 mb-4 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-bold text-amber-800">Animal nuevo — VID: <span className="text-lg">{scanInput.trim()}</span></p>
                <p className="text-xs text-amber-600 mt-1">No registrado. Completa los datos para crearlo.</p>
              </div>
              <button onClick={resetParaSiguiente} className="text-gray-400 hover:text-red-500 text-sm">Cancelar</button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Sexo *</label>
                <select value={newAnimalData.sexo} onChange={(e) => setNewAnimalData(p => ({ ...p, sexo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required data-testid="new-sexo">
                  <option value="">Seleccionar...</option>
                  <option value="hembra">Hembra</option>
                  <option value="macho">Macho</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Raza</label>
                <select value={newAnimalData.raza} onChange={(e) => setNewAnimalData(p => ({ ...p, raza: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="new-raza">
                  <option value="Angus">Angus</option>
                  <option value="Hereford">Hereford</option>
                  <option value="Brangus">Brangus</option>
                  <option value="Braford">Braford</option>
                  <option value="Limousin">Limousin</option>
                  <option value="Charolais">Charolais</option>
                  <option value="Cruza">Cruza</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Categoria</label>
                <select value={newAnimalData.categoria} onChange={(e) => setNewAnimalData(p => ({ ...p, categoria: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="new-categoria">
                  <option value="">Auto (segun sexo)</option>
                  <option value="ternero">Ternero</option>
                  <option value="desmamante">Desmamante</option>
                  <option value="vaquilla">Vaquilla</option>
                  <option value="novillo">Novillo</option>
                  <option value="vaca">Vaca</option>
                  <option value="toro">Toro</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fecha nacimiento</label>
                <input type="date" value={newAnimalData.fecha_nacimiento} onChange={(e) => setNewAnimalData(p => ({ ...p, fecha_nacimiento: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="new-fecha-nac" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                <input type="text" value={newAnimalData.color} onChange={(e) => setNewAnimalData(p => ({ ...p, color: e.target.value }))}
                  placeholder="Negro, Colorado..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="new-color" />
              </div>
            </div>

            {renderFormCampos()}

            <div className="flex justify-end">
              <button
                onClick={guardarRegistroNuevo}
                disabled={guardando || !newAnimalData.sexo}
                className="px-6 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
                data-testid="btn-guardar-nuevo"
              >
                {guardando ? 'Creando...' : 'Crear Animal y Registrar'}
              </button>
            </div>
          </div>
        )}

        {/* Tabla de registros de esta sesion */}
        {registros.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-700">Registros de esta sesion ({registros.length})</p>
            </div>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs text-gray-500">#</th>
                    <th className="text-left py-2 px-3 text-xs text-gray-500">VID</th>
                    <th className="text-left py-2 px-3 text-xs text-gray-500">Tipo</th>
                    <th className="text-left py-2 px-3 text-xs text-gray-500">Datos</th>
                  </tr>
                </thead>
                <tbody>
                  {registros.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-gray-100">
                      <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                      <td className="py-2 px-3 font-medium">{r.animal_vid}</td>
                      <td className="py-2 px-3">
                        {r.animal_nuevo ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">NUEVO</span>
                          : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Existente</span>}
                      </td>
                      <td className="py-2 px-3 text-gray-500 text-xs">
                        {r.datos?.peso ? `${r.datos.peso}kg` : ''}
                        {r.datos?.tipo_servicio ? r.datos.tipo_servicio : ''}
                        {r.datos?.cria_vid ? `Cria: ${r.datos.cria_vid}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── RENDER: DETALLE DE SESION ──────────────
  if (vista === 'detalle' && sesionDetalle) {
    return (
      <div className="p-4 sm:p-6 space-y-4" data-testid="sesion-detalle">
        <button onClick={() => { setVista('lista'); setSesionDetalle(null); }} className="text-gray-500 hover:text-gray-700 mb-2 text-sm flex items-center gap-1">
          ← Volver a sesiones
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{sesionDetalle.nombre}</h1>
            <p className="text-sm text-gray-500">
              {sesionDetalle.tipo.charAt(0).toUpperCase() + sesionDetalle.tipo.slice(1)} | {sesionDetalle.created_at?.slice(0, 10)} | {sesionDetalle.total_animales} animales
              {sesionDetalle.estado === 'activa' && <span className="ml-2 text-green-600 font-semibold">EN CURSO</span>}
            </p>
          </div>
          <div className="flex gap-2">
            {sesionDetalle.estado === 'activa' && (
              <button onClick={() => reanudarSesion(sesionDetalle)} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                Reanudar
              </button>
            )}
            <button onClick={() => descargar(sesionDetalle, 'excel')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="detalle-excel-btn">
              Descargar Excel
            </button>
            <button onClick={() => descargar(sesionDetalle, 'pdf')} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700" data-testid="detalle-pdf-btn">
              Descargar PDF
            </button>
            <button onClick={() => setDeleteConfirm(sesionDetalle.id)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50">
              Eliminar
            </button>
          </div>
        </div>

        {/* Records table */}
        {(sesionDetalle.registros?.length || 0) > 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs text-gray-500 uppercase">#</th>
                  <th className="text-left py-3 px-4 text-xs text-gray-500 uppercase">VID</th>
                  <th className="text-left py-3 px-4 text-xs text-gray-500 uppercase">Estado</th>
                  <th className="text-left py-3 px-4 text-xs text-gray-500 uppercase">Datos Principales</th>
                  <th className="text-left py-3 px-4 text-xs text-gray-500 uppercase">Hora</th>
                </tr>
              </thead>
              <tbody>
                {sesionDetalle.registros.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-400">{i + 1}</td>
                    <td className="py-3 px-4 font-semibold">{r.animal_vid}</td>
                    <td className="py-3 px-4">
                      {r.animal_nuevo
                        ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">NUEVO</span>
                        : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">Existente</span>}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-xs">
                      {r.datos?.peso && `Peso: ${r.datos.peso}kg`}
                      {r.datos?.altura_cadera && ` | Alt: ${r.datos.altura_cadera}cm`}
                      {r.datos?.frame && ` | Frame: ${r.datos.frame}`}
                      {r.datos?.tipo_servicio && `Servicio: ${r.datos.tipo_servicio}`}
                      {r.datos?.toro && ` | Toro: ${r.datos.toro}`}
                      {r.datos?.cria_vid && `Cria VID: ${r.datos.cria_vid}`}
                      {r.datos?.sexo_cria && ` | Sexo: ${r.datos.sexo_cria}`}
                      {r.datos?.ce && `CE: ${r.datos.ce}cm`}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-400">{r.timestamp?.slice(11, 16) || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <p className="text-gray-500">No hay registros en esta sesion</p>
          </div>
        )}

        {/* Delete confirm */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Eliminar Sesion</h3>
              <p className="text-sm text-gray-600 mb-4">Se eliminara permanentemente esta sesion y todos sus registros.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-gray-600 text-sm">Cancelar</button>
                <button onClick={() => eliminarSesion(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="confirm-delete-btn">Eliminar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderFormCampos() {
    const tipo = sesionActiva?.tipo;
    if (tipo === 'pesaje') return <FormPesaje formDatos={formDatos} setFormDatos={setFormDatos} />;
    if (tipo === 'servicio') return <FormServicio formDatos={formDatos} setFormDatos={setFormDatos} />;
    if (tipo === 'paricion') return <FormParicion formDatos={formDatos} setFormDatos={setFormDatos} />;
    if (tipo === 'destete') return <FormDestete formDatos={formDatos} setFormDatos={setFormDatos} animalEncontrado={animalEncontrado} />;
    if (tipo === 'control_macho') return <FormControlMacho formDatos={formDatos} setFormDatos={setFormDatos} />;
    // Sanitarios: vacunacion, enfermedad, medicamento, mortandad, lesion, observacion
    return <FormSanitario formDatos={formDatos} setFormDatos={setFormDatos} tipoSesion={tipo} />;
  }

  return null;
};

export default SesionesTrabajoPage;
